
import Vue from "vue";
import { Alert } from "@/commons/types";
import Alerter from "./Alerter.vue";
import {
  VehicleSchema,
  DeviceHistorySchema,
  DeviceHistoryUpdateSchema,
  DeviceHistoriesService,
} from "@/commons/client";

export default Vue.extend({
  name: "VehicleAssignmentDialog",
  components: { Alerter },

  props: {
    deviceHistory: {
      type: Object as () => DeviceHistorySchema,
      required: true,
    },
  },

  data: () => ({
    dialog: false as boolean,
    assignedVehicleId: null as string | null,
    alerts: [] as Alert[],
    vehicleSearch: "" as string,
    confirmUnassign: false as boolean,
    loadingUnassign: false as boolean,
  }),

  computed: {
    ctaLabel(): string {
      return this.deviceHistory.current_vehicle_id
        ? "Riassegna veicolo"
        : "Assegna veicolo";
    },
    organizationId(): number {
      return this.$store.state.organizationId;
    },
    vehicles(): VehicleSchema[] {
      return this.$store.state.vehicles;
    },
    deviceHistories(): DeviceHistorySchema[] {
      return this.$store.state.deviceHistories;
    },
    // Mappa veloce: vehicle_id -> deviceHistory che lo sta usando (se esiste)
    assignedByVehicleId(): Record<string, DeviceHistorySchema> {
      const map: Record<string, DeviceHistorySchema> = {};
      for (const dh of this.deviceHistories) {
        if (dh.current_vehicle_id) map[dh.current_vehicle_id] = dh;
      }
      return map;
    },
    filteredVehicles(): VehicleSchema[] {
      if (!this.vehicleSearch) return this.vehicles;
      const q = this.vehicleSearch.toLowerCase().trim();
      return this.vehicles.filter((v) =>
        (v.vehicle_id || "").toLowerCase().includes(q)
      );
    },
    selectedIsOccupied(): boolean {
      if (!this.assignedVehicleId) return false;
      const occupant = this.assignedByVehicleId[this.assignedVehicleId];
      return !!(occupant && occupant.uid !== this.deviceHistory.uid);
    },
    occupantDevice(): DeviceHistorySchema | null {
      const vid = this.assignedVehicleId;
      if (!this.selectedIsOccupied || !vid) return null;
      return this.assignedByVehicleId[String(vid)] || null;
    },
    occupantDeviceId(): string | null {
      return this.occupantDevice
        ? this.occupantDevice.current_device_id || this.occupantDevice.uid
        : null;
    },

    // Opzione 'nessuno'
    noneValue(): null {
      // se il backend NON accetta null, sostituisci con return "" as any;
      return null;
    },
    disassociateLabel(): string {
      return "— Nessuno (rimuovi associazione)";
    },
    selectedIsNone(): boolean {
      return this.assignedVehicleId === null || this.assignedVehicleId === "";
    },
    selectedIsNoneAndWasAssigned(): boolean {
      return this.selectedIsNone && !!this.deviceHistory.current_vehicle_id;
    },

    // Abilita conferma se c'è una modifica (anche verso 'nessuno')
    canSubmit(): boolean {
      const current = this.deviceHistory.current_vehicle_id || null;
      const selected = this.assignedVehicleId || null;
      return selected !== current;
    },

    alerterClass() {
      return this.alerts.length > 0 ? "my-2" : "";
    },
    hasLimitedDataAccess(): boolean {
      return this.$store.state.user.has_limited_data_access;
    },
  },

  methods: {
    // Espone metodi per il parent (lista)
    open() {
      this.dialog = true;
    },
    openUnassign() {
      this.dialog = true;
      // se già aperto, mostro direttamente conferma
      this.$nextTick(() => (this.confirmUnassign = true));
    },

    vehicleLabel(v: VehicleSchema): string {
      const occupant = this.assignedByVehicleId[v.vehicle_id];
      if (!occupant) return `${v.vehicle_id} (libero)`;
      const dev = occupant.current_device_id || occupant.uid;
      const isSelf = occupant.uid === this.deviceHistory.uid;
      return isSelf
        ? `${v.vehicle_id} (già su questo dispositivo)`
        : `${v.vehicle_id} (occupato da ${dev})`;
    },

    buildUpdatePayload(
      base: DeviceHistorySchema,
      vehicleId: string | null
    ): DeviceHistoryUpdateSchema {
      return {
        ...base,
        current_vehicle_id: vehicleId as any, // usa "" se l'API non supporta null
      };
    },

    async onConfirm(): Promise<void> {
      this.alerts = [];

      const selected = this.assignedVehicleId || null;
      const current = this.deviceHistory.current_vehicle_id || null;

      if (selected === current) {
        this.alerts.push({
          type: "error",
          text: "Nessuna modifica da salvare.",
        });
        return;
      }

      try {
        // 1) Se selezionato un veicolo occupato da un altro device → liberalo
        if (selected && this.selectedIsOccupied && this.occupantDevice) {
          const prev = this.occupantDevice;
          const prevPayload = this.buildUpdatePayload(prev, this.noneValue);
          await DeviceHistoriesService.deviceHistoryUpdateV1DeviceHistoriesPkPut(
            prev.uid,
            this.organizationId,
            prevPayload
          );
          this.$store.commit("updateDeviceHistory", prevPayload);
        }

        // 2) Se scelto 'nessuno' → disassocia il device corrente
        if (!selected) {
          const newPayload = this.buildUpdatePayload(
            this.deviceHistory,
            this.noneValue
          );
          await DeviceHistoriesService.deviceHistoryUpdateV1DeviceHistoriesPkPut(
            this.deviceHistory.uid,
            this.organizationId,
            newPayload
          );
          this.$store.commit("updateDeviceHistory", newPayload);
        } else {
          // 3) Altrimenti associa/riassegna al veicolo scelto
          const newPayload = this.buildUpdatePayload(
            this.deviceHistory,
            selected
          );
          await DeviceHistoriesService.deviceHistoryUpdateV1DeviceHistoriesPkPut(
            this.deviceHistory.uid,
            this.organizationId,
            newPayload
          );
          this.$store.commit("updateDeviceHistory", newPayload);
        }

        this.dialog = false;
        this.assignedVehicleId = this.noneValue;
      } catch (err) {
        this.alerts.push({
          type: "error",
          text: "Errore durante il salvataggio. Riprova.",
        });
      }
    },

    async onUnassign() {
      this.alerts = [];
      try {
        this.loadingUnassign = true;
        const payload = this.buildUpdatePayload(
          this.deviceHistory,
          this.noneValue
        );
        await DeviceHistoriesService.deviceHistoryUpdateV1DeviceHistoriesPkPut(
          this.deviceHistory.uid,
          this.organizationId,
          payload
        );
        this.$store.commit("updateDeviceHistory", payload);
        this.confirmUnassign = false;
        this.dialog = false;
        this.assignedVehicleId = this.noneValue;
      } catch (err) {
        this.alerts.push({
          type: "error",
          text: "Errore durante lo scollegamento. Riprova.",
        });
      } finally {
        this.loadingUnassign = false;
      }
    },
  },

  watch: {
    dialog(open: boolean) {
      if (open) {
        // Pre-seleziona lo stato corrente (null se libero)
        this.assignedVehicleId =
          this.deviceHistory.current_vehicle_id || this.noneValue;
        this.vehicleSearch = "";
        this.alerts = [];
        this.confirmUnassign = false;
      }
    },
  },
});
